import React from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Popconfirm, Space, Table, Tooltip, Typography } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import { DeliveryTag } from './tags'
import { canResend, formatDateTime } from './utils'

/* eslint-disable react/display-name */
const columns = [
  {
    title: 'Report',
    key: 'part',
    // Blank for a message about the order rather than about a file — a `no_data` order has nothing
    // to attach and is still worth recording as told.
    render: (_, row) => row.partLabel || <Typography.Text type="secondary">The order</Typography.Text>
  },
  {
    title: 'Outcome',
    key: 'status',
    render: (_, row) => (
      <Space direction="vertical" size={0}>
        <DeliveryTag status={row.status} />
        {row.error ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {row.error}
          </Typography.Text>
        ) : null}
      </Space>
    )
  },
  {
    title: 'Tries',
    key: 'attempts',
    render: (_, row) => row.attempts || 0
  },
  {
    title: 'When',
    key: 'when',
    render: (_, row) => formatDateTime(row.updatedAt || row.createdAt)
  }
]
/* eslint-enable react/display-name */

// Whether the customer actually got their report, and a way to send it again.
//
// The rows are the once-only guarantee made visible: the notifier writes one per report per
// channel so that a second sweep cannot send somebody two copies of a PDF they paid for once.
// Until they are on a screen, "sent" and "never tried" look exactly the same from here.
//
// "Send again" is a re-send and never a re-sale. It is offered only when it would do something —
// the report exists and the customer has a conversation to receive it in — because a button that
// the server will refuse is worse than no button when somebody is on the phone to a customer.
const DeliveriesCard = ({ order, saving, onAct }) => {
  const deliveries = order.deliveries || []
  const allowed = canResend(order)

  const button = (
    <Popconfirm
      title="Send the report to this customer again on WhatsApp?"
      okText="Send it"
      cancelText="No"
      disabled={!allowed || saving}
      onConfirm={() => onAct({ kind: 'resend_delivery' })}
    >
      <Button icon={<SendOutlined />} disabled={!allowed} loading={saving} size="small">
        Send again
      </Button>
    </Popconfirm>
  )

  return (
    <Card
      title="Sent to the customer"
      style={{ marginBottom: 16 }}
      extra={
        allowed ? (
          button
        ) : (
          <Tooltip
            title={
              order.paid
                ? 'There is nothing to send yet, or this customer has no WhatsApp conversation with us.'
                : 'Nothing has been paid for on this order.'
            }
          >
            {button}
          </Tooltip>
        )
      }
    >
      {deliveries.length ? (
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={deliveries}
          columns={columns}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <Typography.Text type="secondary">
          Nothing has been sent yet. A report is sent as soon as it is ready.
        </Typography.Text>
      )}
    </Card>
  )
}

DeliveriesCard.propTypes = {
  order: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  onAct: PropTypes.func.isRequired
}

DeliveriesCard.defaultProps = { saving: false }

export default DeliveriesCard
